import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { BRIEFING_CREATE_PATH } from "@/components/scheduled-briefings/briefings-index/BriefingsIndexHeader";
import { ContinuousA4Preview } from "@/components/scheduled-briefings/template-studio/ContinuousA4Preview";
import { A4_PREVIEW_WIDTH_PX } from "@/components/ui/report/TemplateStudioEmptyCanvas/TemplateStudioEmptyCanvas";
import useFeature from "@/lib/feature-management/useFeature";
import { getDisplaySpecFromDraft } from "@/lib/template-studio/draft/templateStudioDraft";
import { resolveReportThemeFromLayoutTheme } from "@/lib/template-studio/reportThemeFromLayoutTheme";
import useAuth, { type AuthState } from "@/store/auth.slice";
import type {
  TemplateLayout,
  TemplateLayoutSpec,
} from "@/types/template-layout/templateLayout.types";
import type { UserInfo } from "@/types/user/userInfo";
import { TemplatePreviewHeader } from "./TemplatePreviewHeader";
import {
  TemplatePreviewStructureRow,
  type TemplatePreviewStructureRowProps,
} from "./TemplatePreviewStructureRow";

export const deriveTemplatePreviewStructureBlocks = (
  spec: TemplateLayoutSpec,
): TemplatePreviewStructureRowProps[] => {
  const root = spec.elements[spec.root];
  const blockIds = root?.children?.length ? root.children : [spec.root];

  return blockIds.flatMap((blockId, index) => {
    const element = spec.elements[blockId];
    if (!element) return [];

    const title = element.props?.title;
    const normalizedTitle =
      typeof title === "string" && title.trim() ? title.trim() : undefined;
    return [
      {
        order: index + 1,
        componentType: element.type,
        ...(normalizedTitle &&
          (element.type === "BarChart"
            ? { description: normalizedTitle }
            : { title: normalizedTitle })),
      },
    ];
  });
};

/**
 * Maps a layout owner id to a display name when it matches the signed-in user.
 * @param createdByUserId - Layout `createdBy` user id from the repository.
 * @param user - Current authenticated user from auth state.
 */
export const resolveTemplatePreviewCreatedByDisplayName = (
  createdByUserId: string,
  user: Pick<UserInfo, "id" | "name">,
): string | undefined => {
  if (String(user.id) !== createdByUserId) return undefined;
  const displayName = user.name.trim();
  return displayName.length > 0 ? displayName : undefined;
};

interface TemplatePreviewProps {
  template: TemplateLayout;
}

const TemplatePreview = ({ template }: TemplatePreviewProps) => {
  const navigate = useNavigate();
  const canArchiveTemplates = useFeature(
    "enable-scheduled-briefings-v1-1",
    false,
  );
  const user = useAuth((state: AuthState) => state.user);
  const createdByDisplayName = resolveTemplatePreviewCreatedByDisplayName(
    template.createdBy,
    user,
  );
  const structureBlocks = deriveTemplatePreviewStructureBlocks(
    template.document.spec,
  );
  const previewTheme = resolveReportThemeFromLayoutTheme(
    template.document.theme,
  );
  const previewSpec = getDisplaySpecFromDraft(template.document);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <TemplatePreviewHeader
        title={template.name}
        description={template.description}
        createdByDisplayName={createdByDisplayName}
        updatedLabel={`Updated ${format(new Date(template.updatedAt), "MMM d yyyy")}`}
        isArchived={template.status === "archived"}
        showArchivedBadge={canArchiveTemplates}
        onCreateBriefing={() =>
          void navigate({
            to: BRIEFING_CREATE_PATH,
            search: { templateId: template.id },
          })
        }
      />

      <main className="flex min-h-0 flex-1 overflow-hidden bg-[#fafafa]">
        <section
          aria-label="Template structure"
          className="w-133 shrink-0 overflow-y-auto px-16 py-8"
        >
          <div className="flex w-full max-w-[404px] flex-col gap-3">
            <div className="flex items-center justify-between gap-3 pb-4">
              <p className="text-xs font-medium tracking-[0.12px] text-gray-600 uppercase">
                Structure
              </p>
              <p className="text-xs tracking-[0.12px] text-gray-600">
                {structureBlocks.length}{" "}
                {structureBlocks.length === 1 ? "section" : "sections"}
              </p>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {structureBlocks.map((block) => (
                <TemplatePreviewStructureRow key={block.order} {...block} />
              ))}
            </div>
          </div>
        </section>

        <section
          aria-label="Template preview"
          className="flex min-w-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-16 py-8">
            <div
              className="mx-auto flex w-full flex-col gap-3"
              style={{ maxWidth: A4_PREVIEW_WIDTH_PX }}
            >
              <div className="flex items-start justify-between gap-6 pb-5">
                <p className="text-[11px] font-semibold tracking-[0.44px] text-gray-600 uppercase">
                  Preview
                </p>
                <p className="max-w-md text-right text-xs tracking-[0.12px] text-gray-600">
                  Sample data — live editions use your briefing's instructions
                </p>
              </div>
              <div className="relative min-h-0 w-full">
                <ContinuousA4Preview spec={previewSpec} theme={previewTheme} />
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TemplatePreview;
